import { Suspense, lazy, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import MarketingLayout from './pages/marketing/components/MarketingLayout.jsx'

// PRE-LAUNCH ROUTING (2026-09-21).
//
// claimguru.com serves ONE product page: the coming soon page. ClaimGuru is
// software, not a public adjuster, and until the product is generally
// available a marketing site cannot make claims the product can be held to.
//
// LandingPage, BenefitsPage, PricingPage and AboutPage are therefore NOT
// routed and NOT imported, so their copy ships in no bundle and is served to
// nobody. The files stay in the tree because they are the starting point for
// the launch site, and their copy MUST be re-audited against the positioning
// line ("ClaimGuru is the claims operating system for public adjusters")
// before any of them is routed again. Their route URLs 301 to "/" at the
// edge, in netlify.toml.
//
// Legal and trust pages stay reachable at their own URLs. A legal page is
// never removed.
const TermsPage = lazy(() => import('./pages/marketing/TermsPage.jsx'))
const PrivacyPage = lazy(() => import('./pages/marketing/PrivacyPage.jsx'))
const SecurityPage = lazy(() => import('./pages/marketing/SecurityPage.jsx'))
const ComingSoonPage = lazy(() => import('./pages/marketing/ComingSoonPage.jsx'))
const NotFoundPage = lazy(() => import('./pages/marketing/NotFoundPage.jsx'))

// /signup and /login live on app.claimguru.com (Clerk-hosted). Existing links
// and old emails still point at the marketing-host URLs, so they bounce to the
// app host rather than dead-ending on this site.
const APP_HOST = 'https://app.claimguru.com'
function ExternalRedirect({ to }) {
  useEffect(() => { window.location.replace(to) }, [to])
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--v3-canvas)' }}>
      <p className="text-sm" style={{ color: 'var(--v3-text-muted)', fontFamily: 'var(--v3-font-mono)' }}>
        REDIRECTING…
      </p>
    </div>
  )
}

// There is deliberately no VITE_COMING_SOON env flag any more. The pre-launch
// state of a LIVE public site is not something that should depend on a value
// set in a dashboard nobody reads: it is in the code, where a diff shows it.
// Relaunching means changing this file, not flipping a variable.

// Quiet suspense fallback: a canvas-colored screen with a subtle pulsing dot
// instead of a visible "LOADING…" text flash on every lazy route (live-audit
// 2026-07-02 flagged the flash on /privacy). Screen readers still get a label.
const Loader = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: 'var(--v3-canvas)' }}
    role="status"
    aria-live="polite"
  >
    <div
      className="h-2 w-2 rounded-full animate-pulse"
      style={{ background: 'var(--v3-text-disabled)' }}
      aria-hidden="true"
    />
    <span className="sr-only">Loading</span>
  </div>
)

export default function App() {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* Auth pages live on the firm app. Bounce the marketing-host URLs
            that old emails and cached links still point at (avoids the
            audit-found dead #root on /signup). */}
        <Route path="signup" element={<ExternalRedirect to={`${APP_HOST}/sign-up`} />} />
        <Route path="sign-up" element={<ExternalRedirect to={`${APP_HOST}/sign-up`} />} />
        <Route path="login" element={<ExternalRedirect to={APP_HOST} />} />
        <Route path="sign-in" element={<ExternalRedirect to={APP_HOST} />} />

        {/* The whole public product surface, pre-launch. Carries the email
            signup, which is the only live lead path on this domain. */}
        <Route index element={<ComingSoonPage />} />

        {/* Retired product-marketing URLs. netlify.toml 301s them at the
            edge, so a real visitor never reaches these; this keeps a
            client-side navigation landing somewhere accurate too. */}
        <Route path="benefits" element={<ComingSoonPage />} />
        <Route path="pricing" element={<ComingSoonPage />} />
        <Route path="about" element={<ComingSoonPage />} />

        {/* Legal and trust pages stay at their own URLs, with chrome. */}
        <Route element={<MarketingLayout />}>
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="security" element={<SecurityPage />} />
          {/* Unknown paths render the 404 page (noindex). The real HTTP 404
              status is produced by Netlify, see netlify.toml. Keep this in
              sync with the enumerated known-route redirects there. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
